//
// arc.ts — the tiered arc menu
//
// Replaces `dial.ts`, the single-strip circular dial. Build 1.5, item 6.
//
// Dave: *"the circular menu must change. We will use the akaspace method, a
// tiered arc button menu, with 5 original buttons, and a scrolling circular
// dial for 3 things… You can use the bare bones of the akaspace menu and
// replace the menu at both website and iOS app."*
//
// The shape, seat for seat, is `~/akaspace-web/src/hud/arcMenu.ts`. What is
// AZISUR's own:
//
//   - the second tier is the **vault** shape (four seats that stay lit),
//     not the toolkit shape (seven seats that fire once). Dave asked for
//     this by name.
//   - the outer lane carries **visibility** and the **timeline** instead of
//     brightness and star magnitude.
//   - the five first-tier seats are four genres plus settings.
//
// Rules inherited from `docs/contracts/dial-hud.md` that still bind:
//   §6 transparent, but readable — no filled panels behind the arc.
//   §7 a transparent block still consumes taps — hence `pointer-events`
//      none on the layer, auto on the controls.
//   §8 **never redraw a control mid-drag** — see `liveDrag`. The whole SVG
//      is rebuilt on every state change, which would delete the element
//      holding the pointer capture and kill the gesture on its first move.
//      Do not "simplify" this away.
//
// Mirrors `Views/Arc/ArcMenu.swift`.
//

import { MIN_ZOOM, MAX_ZOOM, type AppState } from "../state";
import type { ContactKey, GenreId } from "../types";
import { GENRES, SETTINGS_SEAT, keysOfGenre, labelFor } from "./arc-catalog";
import {
  type ArcTier,
  CREST_ART,
  PAN_SENS,
  POLARITY_R,
  TIER1_SEAT,
  TIER2_SEAT,
  TIMELINE_SENS,
  VISIBILITY_SENS,
  ZOOM_SENS,
  apex,
  arcPath,
  crestOpen,
  laneBounds,
  layout as makeLayout,
  menuHeight,
  phiOf,
  polarity,
  scaleFor,
  seat,
  tier2Seat,
} from "./arc-layout";

export type MenuDock = "left" | "right";

const PREF_DOCK = "azisur.menu.dock";
const PREF_SCALE = "azisur.menu.scale";
const PREF_SHIFT = "azisur.menu.shift";

function loadPref(key: string, fallback: number): number {
  const raw = localStorage.getItem(key);
  const n = raw === null ? NaN : Number(raw);
  return Number.isFinite(n) ? n : fallback;
}

export interface ArcHooks {
  /** Settings was tapped — the host opens its HUD list. */
  onSettings: () => void;
  /** The margin side changed, so the chrome must follow. Item 10. */
  onDock: (dock: MenuDock) => void;
}

export class ArcMenu {
  private tier: ArcTier = "tier1";
  private openGenre: GenreId | null = null;

  /**
   * Which layer the visibility lane acts on, in Dave's order of precedence:
   * a contact selected on the map, else the second-tier button last tapped,
   * else the whole open genre.
   */
  private focusKey: ContactKey | null = null;

  private get polarityOn(): boolean {
    return this.state.polarityOn;
  }

  private userScale = loadPref(PREF_SCALE, 1);
  private shift = loadPref(PREF_SHIFT, 0);
  private dock: MenuDock = localStorage.getItem(PREF_DOCK) === "left" ? "left" : "right";

  private armed: "s" | "p" | null = null;
  private liveDrag = false;
  private lastPhi: Array<number | null> = [null, null, null];

  constructor(
    private root: HTMLElement,
    private state: AppState,
    private hooks: ArcHooks,
  ) {
    this.root.className = "arc-menu";
    window.addEventListener("resize", () => this.paint());
    window.addEventListener("keydown", (e) => this.onKey(e));
    this.state.subscribe(() => {
      if (!this.liveDrag) this.paint();
    });
    this.paint();
    this.hooks.onDock(this.dock);
  }

  /** How much vertical room the menu is claiming, so the map can clear it. */
  height(): number {
    return menuHeight(this.tier, scaleFor(window.innerWidth) * this.userScale);
  }

  dockSide(): MenuDock {
    return this.dock;
  }

  setDock(dock: MenuDock): void {
    this.dock = dock;
    localStorage.setItem(PREF_DOCK, dock);
    this.hooks.onDock(dock);
    this.paint();
  }

  // --- what the lanes act on -------------------------------------------

  /**
   * The layers the visibility lane moves. A single contact selected on the
   * map wins; then the last second-tier button tapped; then everything in
   * the open genre. Dave: *"A whole catagory responds, or a single item is
   * resonding if it is selected on the stage."*
   */
  private targetKeys(): ContactKey[] {
    const sel = this.state.selectedObject?.layer;
    if (sel) return [sel];
    if (this.focusKey) return [this.focusKey];
    if (this.openGenre) return keysOfGenre(this.openGenre);
    return [];
  }

  /** Named so the HUD readout can say what the visibility lane is holding. */
  targetLabel(): string {
    const keys = this.targetKeys();
    if (keys.length === 0) return "---";
    if (this.state.selectedObject?.layer) return "OBJ";
    if (keys.length === 1) return labelFor(keys[0]).slice(0, 9).toUpperCase();
    return (this.openGenre ?? "").toUpperCase();
  }

  private targetOpacity(): number {
    const keys = this.targetKeys();
    if (keys.length === 0) return 0;
    const sum = keys.reduce((a, k) => a + this.state.layers.opacity[k], 0);
    return sum / keys.length;
  }

  // --- painting ---------------------------------------------------------

  private applyChrome(): void {
    this.root.dataset.dock = this.dock;
    this.root.dataset.armed = this.armed ?? "";
    this.root.style.setProperty("--menu-shift", `${this.shift * 100}%`);
  }

  paint(): void {
    // §8: a rebuild mid-drag deletes the node holding pointer capture.
    if (this.liveDrag) {
      this.applyChrome();
      return;
    }

    const w = window.innerWidth;
    const h = window.innerHeight;
    const l = makeLayout(w, h, this.userScale);
    this.root.style.setProperty("--live-scale", "1");
    this.applyChrome();

    if (this.tier === "hidden") {
      this.root.classList.add("is-hidden");
      this.root.innerHTML = `<button type="button" class="arc-hook is-alone" data-hook="1" title="show menu" style="left:${w / 2}px;top:${h - 18}px;width:${36 * l.scale}px;height:${36 * l.scale}px"><img src="/waveCrestSmall.svg" alt="menu" /></button>`;
      this.root.querySelector<HTMLButtonElement>("[data-hook]")!.addEventListener("click", () => {
        this.tier = "tier1";
        this.paint();
      });
      return;
    }
    this.root.classList.remove("is-hidden");

    const html =
      this.rings(l) +
      this.tier1(l) +
      this.tier2(l) +
      this.polarityButton(l) +
      this.crest(l);
    this.root.innerHTML = html;
    this.bind(l);
  }

  private laneLabels(): string[] {
    const s = this.state;
    if (this.polarityOn) {
      const mins = Math.round(-s.timeOffsetHours * 60);
      const time = mins === 0 ? "NOW" : `T-${String(Math.floor(mins / 60)).padStart(2, "0")}h${String(mins % 60).padStart(2, "0")}`;
      return [time, this.zoomLabel(), s.canPan ? "←   →" : "· · ·"];
    }
    const keys = this.targetKeys();
    const vis = keys.length === 0 ? "---" : String(Math.round(this.targetOpacity())).padStart(3, "0");
    return [vis, this.zoomLabel(), s.canPan ? "←   →" : "· · ·"];
  }

  private zoomLabel(): string {
    const pct = ((this.state.zoomScale - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM)) * 1000;
    return String(Math.round(pct)).padStart(4, "0");
  }

  private rings(l: ReturnType<typeof makeLayout>): string {
    const names = this.laneLabels();
    const dead = [false, false, !this.state.canPan];
    const body = [0, 1, 2]
      .map((i) => {
        const b = laneBounds(l.scale, i);
        const tip = apex(l.laneCx, l.laneCy, b.mid);
        const cls = dead[i] ? " is-dead" : "";
        return `<path class="arc-dial-edge${cls}" d="${arcPath(l.laneCx, l.laneCy, b.outer)}" />
          <path class="arc-dial-edge is-inner${cls}" d="${arcPath(l.laneCx, l.laneCy, b.inner)}" />
          <path class="arc-dial-hit${cls}" data-lane="${i}" d="${arcPath(l.laneCx, l.laneCy, b.mid)}" />
          <text class="arc-dial-label${cls}" x="${tip.x.toFixed(1)}" y="${tip.y.toFixed(1)}" text-anchor="middle" dominant-baseline="middle" font-size="${12 * l.scale}">${names[i]}</text>`;
      })
      .join("");
    return `<svg class="arc-rings" viewBox="0 0 ${l.w} ${l.h}" width="${l.w}" height="${l.h}" aria-hidden="true">${body}</svg>`;
  }

  /**
   * A genre seat lights when anything under it is showing — so the arc
   * always says what is on the map, whether or not its tier is open.
   */
  private genreLit(id: GenreId): boolean {
    return keysOfGenre(id).some((k) => this.state.layers.on[k]);
  }

  private tier1(l: ReturnType<typeof makeLayout>): string {
    const d = TIER1_SEAT * l.scale;
    const items = [
      ...GENRES.map((g) => ({ id: g.id as string, label: g.label, icon: g.icon, genre: true })),
      { id: SETTINGS_SEAT.id, label: SETTINGS_SEAT.label, icon: SETTINGS_SEAT.icon, genre: false },
    ];
    return items
      .map((item, i) => {
        const s = seat(l, i);
        const lit = item.genre && this.genreLit(item.id as GenreId);
        const open = this.openGenre === item.id;
        const cls = `arc-btn${lit ? " is-lit" : ""}${open ? " is-open" : ""}`;
        return `<button type="button" class="${cls}" data-tier1="${item.id}" title="${item.label}" style="left:${s.x}px;top:${s.y}px;width:${d}px;height:${d}px;--icon:url('/icons/${item.icon}')"><span class="arc-icon" aria-hidden="true"></span></button>`;
      })
      .join("");
  }

  private tier2(l: ReturnType<typeof makeLayout>): string {
    if (this.tier !== "tier2" || !this.openGenre) return "";
    const genre = GENRES.find((g) => g.id === this.openGenre);
    if (!genre) return "";
    const d = TIER2_SEAT * l.scale;
    return genre.seats
      .map((entry, i) => {
        const s = tier2Seat(l, i);
        const on = this.state.layers.on[entry.key];
        const focused = this.focusKey === entry.key;
        const cls = `arc-btn arc-tier2-btn${on ? " is-lit" : ""}${focused ? " is-focused" : ""}`;
        return `<button type="button" class="${cls}" data-tier2="${entry.key}" title="${entry.label}" style="left:${s.x}px;top:${s.y}px;width:${d}px;height:${d}px;--icon:url('/icons/${entry.icon}')"><span class="arc-icon" aria-hidden="true"></span></button>`;
      })
      .join("");
  }

  private polarityButton(l: ReturnType<typeof makeLayout>): string {
    const p = polarity(l);
    const r = POLARITY_R * l.scale * 2;
    const side = r * 0.78;
    const step = this.state.playbackStep;
    const leftX = p.x - r * 1.2;
    const rightX = p.x + r * 1.2;
    const backLabel = step < 0 ? String(Math.abs(step)) : "◀";
    const fwdLabel = step > 0 ? String(step) : "▶";
    return (
      `<button type="button" class="arc-polarity${step < 0 ? " is-on" : ""}" data-play-back="1" title="rewind" style="left:${leftX}px;top:${p.y}px;width:${side}px;height:${side}px">${backLabel}</button>` +
      `<button type="button" class="arc-polarity${this.polarityOn ? " is-on" : ""}" data-polarity="1" title="polarity — ${this.polarityOn ? "timeline" : "visibility"}" style="left:${p.x}px;top:${p.y}px;width:${r}px;height:${r}px">${this.polarityOn ? "1" : "0"}</button>` +
      `<button type="button" class="arc-polarity${step > 0 ? " is-on" : ""}" data-play-fwd="1" title="toward now" style="left:${rightX}px;top:${p.y}px;width:${side}px;height:${side}px">${fwdLabel}</button>`
    );
  }

  private crest(l: ReturnType<typeof makeLayout>): string {
    const c = crestOpen(l, this.tier);
    const hit = Math.max(56, CREST_ART * l.scale * 1.7);
    const mode = this.armed === "s" ? "SCALE" : this.armed === "p" ? "PLACE" : "";
    return `<button type="button" class="arc-hook" data-hook="1" title="crest · S scale · P place" style="left:${c.x}px;top:${c.y}px;width:${hit}px;height:${hit}px"><img src="/waveCrestSmall.svg" alt="menu" /></button>
      <div class="arc-armed" ${this.armed ? "" : "hidden"}>${mode}</div>`;
  }

  // --- interaction ------------------------------------------------------

  private bind(l: ReturnType<typeof makeLayout>): void {
    this.root.querySelectorAll<HTMLButtonElement>("[data-tier1]").forEach((btn) => {
      btn.addEventListener("click", () => this.pickTier1(btn.dataset.tier1!));
    });
    this.root.querySelectorAll<HTMLButtonElement>("[data-tier2]").forEach((btn) => {
      btn.addEventListener("click", () => this.pickTier2(btn.dataset.tier2 as ContactKey));
    });
    this.root.querySelector<HTMLButtonElement>("[data-play-back]")!.addEventListener("click", () => {
      this.state.tapReverse();
      this.paint();
    });
    this.root.querySelector<HTMLButtonElement>("[data-polarity]")!.addEventListener("click", () => {
      this.state.polarityOn = !this.state.polarityOn;
      this.state.showHUDFeedback(this.state.polarityOn ? "[TIMELINE]" : "[VISIBILITY]");
      this.paint();
    });
    this.root.querySelector<HTMLButtonElement>("[data-play-fwd]")!.addEventListener("click", () => {
      this.state.tapForward();
      this.paint();
    });
    this.bindCrest(this.root.querySelector<HTMLButtonElement>("[data-hook]")!);
    this.bindLanes(l);
  }

  /**
   * Dave: *"as each of the main 5 buttons are pressed, nothing is visible,
   * we are just activating the 2nd tier of buttons."* A genre button opens
   * its row and never switches a layer on by itself.
   */
  private pickTier1(id: string): void {
    if (id === "settings") {
      this.hooks.onSettings();
      return;
    }
    const genre = id as GenreId;
    if (this.openGenre === genre && this.tier === "tier2") {
      this.tier = "tier1";
      this.openGenre = null;
    } else {
      this.openGenre = genre;
      this.tier = "tier2";
      this.focusKey = null;
    }
    this.paint();
  }

  private pickTier2(key: ContactKey): void {
    const layers = this.state.layers;
    layers.on[key] = !layers.on[key];
    this.focusKey = key;
    this.state.showHUDFeedback(`[${labelFor(key).toUpperCase()}] — ${layers.on[key] ? "ON" : "OFF"}`);
    this.state.notify();
    this.paint();
  }

  private bindLanes(l: ReturnType<typeof makeLayout>): void {
    this.root.querySelectorAll<SVGPathElement>("[data-lane]").forEach((path) => {
      const index = Number(path.dataset.lane);
      const mid = laneBounds(l.scale, index).mid;
      path.addEventListener("pointerdown", (e) => {
        if (index === 2 && !this.state.canPan) return;
        e.stopPropagation();
        path.setPointerCapture(e.pointerId);
        this.liveDrag = true;
        if (index === 0 && this.polarityOn) this.state.timelineHeld = true;
        this.lastPhi[index] = phiOf(e.clientX, e.clientY, l.laneCx, l.laneCy);
      });
      path.addEventListener("pointermove", (e) => {
        if (this.lastPhi[index] === null) return;
        const phi = phiOf(e.clientX, e.clientY, l.laneCx, l.laneCy);
        const last = this.lastPhi[index]!;
        this.lastPhi[index] = phi;
        this.applySlide(index, (phi - last) * mid);
      });
      const end = () => {
        if (this.lastPhi[index] === null) return;
        this.lastPhi[index] = null;
        this.liveDrag = false;
        if (index === 0) this.state.timelineHeld = false;
        this.paint();
      };
      path.addEventListener("pointerup", end);
      path.addEventListener("pointercancel", end);
    });
  }

  private applySlide(index: number, arc: number): void {
    const s = this.state;

    if (index === 0) {
      if (this.polarityOn) {
        // The global timeline. Dave: "on 1, it toggles to the global
        // time-line that can be paused, and moved on it's path."
        s.setTimeOffsetHours(s.timeOffsetHours + arc * TIMELINE_SENS);
      } else {
        const keys = this.targetKeys();
        if (keys.length === 0) return;
        for (const k of keys) {
          const next = s.layers.opacity[k] + arc * VISIBILITY_SENS;
          s.layers.opacity[k] = Math.min(100, Math.max(0, next));
        }
      }
    } else if (index === 1) {
      // Build 2.0, item 8: a drag *multiplies* scale so the same
      // gesture is useful at 1× and at 8×.
      s.zoomScale = Math.min(
        MAX_ZOOM,
        Math.max(MIN_ZOOM, s.zoomScale * Math.exp(arc * ZOOM_SENS)),
      );
      s.clampPan();
    } else {
      // Polarity picks the axis, exactly as it does on akaspace.
      if (this.polarityOn) s.panBy(0, arc * PAN_SENS);
      else s.panBy(arc * PAN_SENS, 0);
    }
    this.rewriteLabels();
  }

  /**
   * §8 again: while a lane is being dragged the SVG must not be rebuilt, so
   * the two labels that change are edited in place instead.
   */
  private rewriteLabels(): void {
    const names = this.laneLabels();
    this.root.querySelectorAll(".arc-dial-label").forEach((el, i) => {
      el.textContent = names[i];
    });
  }

  private bindCrest(hook: HTMLButtonElement): void {
    let dragging = false;
    let moved = 0;
    let lastX = 0;
    let startY = 0;
    let startScale = this.userScale;

    hook.addEventListener("pointerdown", (e) => {
      if (!this.armed) return;
      e.preventDefault();
      e.stopPropagation();
      dragging = true;
      this.liveDrag = true;
      moved = 0;
      lastX = e.clientX;
      startY = e.clientY;
      startScale = this.userScale;
      hook.setPointerCapture(e.pointerId);
    });

    hook.addEventListener("pointermove", (e) => {
      if (!dragging || !this.armed) return;
      const dx = e.clientX - lastX;
      lastX = e.clientX;
      moved += Math.abs(dx);
      if (this.armed === "s") {
        this.userScale = Math.min(1.35, Math.max(0.55, startScale - (e.clientY - startY) * 0.012));
        localStorage.setItem(PREF_SCALE, String(this.userScale));
        this.root.style.setProperty("--live-scale", String(this.userScale / startScale));
        return;
      }
      this.shift = Math.min(0.38, Math.max(-0.38, this.shift + dx / 280));
      localStorage.setItem(PREF_SHIFT, String(this.shift));
      this.applyChrome();
    });

    hook.addEventListener("pointerup", (e) => {
      if (!dragging) return;
      dragging = false;
      this.liveDrag = false;
      if (this.armed === "p") {
        // Placing the menu past the halfway line also moves the margin, so
        // the chrome and the menu always sit on the same side. Item 10.
        const next: MenuDock = e.clientX < window.innerWidth / 2 ? "left" : "right";
        if (next !== this.dock) this.setDock(next);
      }
      this.paint();
    });

    hook.addEventListener("click", (e) => {
      if (moved > 6 || this.armed === "p") {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (this.armed === "s") {
        e.preventDefault();
        this.userScale = Math.min(1.35, Math.max(0.55, this.userScale + (e.shiftKey ? 0.07 : -0.07)));
        localStorage.setItem(PREF_SCALE, String(this.userScale));
        this.paint();
        return;
      }
      this.tier = "hidden";
      this.paint();
    });
  }

  private onKey(e: KeyboardEvent): void {
    if (e.repeat) return;
    const tag = (e.target as HTMLElement).tagName;
    if (tag === "INPUT" || tag === "TEXTAREA") return;
    if (e.key === "s" || e.key === "S") this.armed = this.armed === "s" ? null : "s";
    else if (e.key === "p" || e.key === "P") this.armed = this.armed === "p" ? null : "p";
    else if (e.key === "Escape") this.armed = null;
    else return;
    this.paint();
  }
}
